import apiClient from "./apiClient";
import { DeleteRequest } from "@/types/deleteRequest";
import {SearchParam} from "@/types/searchParam";
import {KindergartenCalculationReport, KindergartenCalculationSearchParam} from "@/types/report";
import {PageResponse} from "@/types/pageResponse";
import {
    KindergartenActReconciliation, KindergartenAnalyticsResponse, KindergartenAnalyticsSearchParam,
    KindergartenPaidAmount,
    KindergartenRetention,
    KindergartenTimesheetData, KindergartenTimesheetGeneralData
} from "@/types/kindergarten";
import {KindergartenActReconciliationStatus} from "@/enums/Kindergarten";
import {KindergartenDetailResponse} from "@/types/lmsService";

const BASE_URL = process.env.VUE_APP_BASE_URL as string;

export async function findKindergartenPaidAmount(
    searchParams: SearchParam
): Promise<PageResponse<KindergartenCalculationReport>> {
    try {
        const response = await apiClient.post<PageResponse<KindergartenCalculationReport>>(
          `${BASE_URL}/api/v1/kindergarten/paid/amount/list`,
            searchParams
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching kindergarten paid amount list:", error);
        throw error;
    }
}

export async function findKindergartenRetention(
    searchParams: SearchParam
): Promise<PageResponse<KindergartenRetention>> {
    try {
        const response = await apiClient.post<PageResponse<KindergartenRetention>>(
            `${BASE_URL}/api/v1/kindergarten/retention/list`,
            searchParams
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching kindergarten retention list:", error);
        throw error;
    }
}

export async function deleteKindergartenRetentionList(
    kindergartenRetentionIdList: number[],
    reason: string
) {
    try {
        const response = await apiClient.post(
            `${BASE_URL}/api/v1/kindergarten/retention/delete`,
            { ids: kindergartenRetentionIdList, reason } as DeleteRequest<number>
        );
        return response.status;
    } catch (error) {
        console.error("Error deleting by kindergarten calculation id list:", error);
        throw error;
    }
}

export async function uploadKindergartenRetentionXlsx(accountingMonth: number, accountingYear: number, formData: FormData) {
    try {
        const response = await apiClient.post(
            `${BASE_URL}/api/v1/kindergarten/retention/save/xlsx/${accountingMonth}/${accountingYear}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then((response) => {
            return response;
        }).catch((error) => {
            return Promise.reject(error);
        });
        return response.data;
    } catch (error) {
        console.log("error on uploading file: ", error);
        throw error;
    }
}

export async function uploadKindergartenPaidAmountXlsx(formData: FormData) {
    try {
        const response = await apiClient.patch(
            `${BASE_URL}/api/v1/kindergarten/paid/amount/update/using/xlsx`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then((response) => {
            return response;
        }).catch((error) => {
            return Promise.reject(error);
        });
        return response.data;
    } catch (error) {
        console.log("error on uploading file: ", error);
        throw error;
    }
}

export async function addKindergartenPaidAmount(
    kindergartenPaidAmount: KindergartenPaidAmount
): Promise<KindergartenPaidAmount> {
    try {
        const response = await apiClient.post<KindergartenPaidAmount>(
          `${BASE_URL}/api/v1/kindergarten/paid/amount/add`,
            kindergartenPaidAmount
        );
        return response.data;
    } catch (error) {
        console.error("Error adding kindergarten paid amount:", error);
        throw error;
    }
}

export async function addFullKindergartenPaidAmount(
    searchParams: SearchParam,
    paidDate: Date
): Promise<number> {
    try {
        const response =  await apiClient.post(
            `${BASE_URL}/api/v1/kindergarten/paid/amount/add/full/list/${paidDate}`,
            searchParams
        );
        return response.status;
    } catch (error) {
        console.log("Error saving full kindergarten paid amount:", error);
        throw error;
    }
}

export async function getFullTimesheetDetail(
    searchParam: SearchParam
): Promise<KindergartenTimesheetData[]> {
    try {
        const response = await apiClient.post<KindergartenTimesheetData[]>(
            `${BASE_URL}/api/v1/timesheet/list`, searchParam
        );
        return response.data;
    } catch (error) {
        console.log("Error on getting full timesheet detail:", error);
        throw error;
    }
}

export async function getFullTimesheetGeneralDetail(
    searchParam: SearchParam
): Promise<KindergartenTimesheetGeneralData[]> {
    try {
        const response = await apiClient.post<KindergartenTimesheetGeneralData[]>(
            `${BASE_URL}/api/v1/timesheet/general/list`, searchParam
        );
        return response.data;
    } catch (error) {
        console.log("Error on getting full timesheet detail:", error);
        throw error;
    }
}


export async function restoreKindergartenRetentionList(
    kindergartenRetentionIdList: number[],
    reason: string
) {
    const response = await apiClient.post(
        `${BASE_URL}/api/v1/kindergarten/retention/restore`,
        { ids: kindergartenRetentionIdList, reason } as DeleteRequest<number>
    );
    return response.status;
}

export async function restoreSelectedTimesheetData(monthlyTimesheetId: string, monthlyTimesheetDatasetId: string[], reason: string) {
    const response = await apiClient.post(
        `${BASE_URL}/api/v1/timesheet/restore/${monthlyTimesheetId}`,
        { ids: monthlyTimesheetDatasetId, reason } as DeleteRequest<string>
    );
    return response.status;
}

export async function deleteSelectedTimesheetData(monthlyTimesheetId: string, monthlyTimesheetDatasetId: string[], reason: string) {
    try {
        const response = await apiClient.post(
            `${BASE_URL}/api/v1/timesheet/delete/${monthlyTimesheetId}`,
            { ids: monthlyTimesheetDatasetId, reason } as DeleteRequest<string>
        );
        return response.status;
    } catch (error) {
        console.log("Error on deleting timesheet detail by id:", monthlyTimesheetDatasetId);
        throw error;
    }
}

/** Удаление табелей целиком (жёсткое, только админ): все строки, общие сведения и шапка. */
export async function removeMonthlyTimesheets(monthlyTimesheetIds: string[], reason: string) {
    const response = await apiClient.post(
        `${BASE_URL}/api/v1/timesheet/remove`,
        { ids: monthlyTimesheetIds, reason } as DeleteRequest<string>
    );
    return response.status;
}

export async function uploadKindergartenActReconciliation(formData: FormData, accountingYear: number) {
    try {
        const response = await apiClient.post(
          `${BASE_URL}/api/v1/kindergarten/act/reconciliation/upload/${accountingYear}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then((response) => {
            return response;
        }).catch((error) => {
            return Promise.reject(error);
        });
        return response.data;
    } catch (error) {
        console.log("Error on uploading kindergarten act reconciliation");
        throw error;
    }
}

export async function deleteKindergartenActReconciliationById(kindergartenActReconciliationId: number, reason: string):Promise<number> {
    try {
        const response = await apiClient.post<number>(
            `${BASE_URL}/api/v1/kindergarten/act/reconciliation/delete`,
            { ids: [kindergartenActReconciliationId], reason } as DeleteRequest<number>
        );
        return response.status;
    } catch (error) {
        console.log("Error on deleting kindergarten act reconciliation by id " + kindergartenActReconciliationId);
        throw error;
    }
}

export async function getDataListByParam(searchParam: SearchParam):Promise<PageResponse<KindergartenActReconciliation>> {
    try {
        const response = await apiClient.post<PageResponse<KindergartenActReconciliation>>(
            `${BASE_URL}/api/v1/kindergarten/act/reconciliation/list`,
            searchParam
        );
        return response.data;
    } catch (error) {
        console.log("Error on getting list of kindergarten act reconciliation");
        throw error;
    }
}

export async function changeKindergartenActReconciliationStatus(
    kindergartenActReconciliationId: number,
    status: KindergartenActReconciliationStatus,
    comment: string
):Promise<KindergartenActReconciliation> {
    try {
        const response = await apiClient.put<KindergartenActReconciliation>(
            `${BASE_URL}/api/v1/kindergarten/act/reconciliation/change/status/${kindergartenActReconciliationId}/${status}`,
            comment
        );
        return response.data;
    } catch (error) {
        console.log("Error on changing status of kindergarten act reconciliation");
        throw error;
    }
}

export async function getPreCalculationList(searchParam: SearchParam): Promise<KindergartenDetailResponse[]> {
    try {
        const response = await apiClient.post<KindergartenDetailResponse[]>(
            `${BASE_URL}/api/v1/timesheet/pre-calculation`,
            searchParam
        );
        return response.data;
    } catch (error) {
        console.log("error on getting pre calculation list", error);
        throw error;
    }
}

export async function runPreCalculationAmount(monthlyTimesheetIdList: string[]) {
    try {
        const response = await apiClient.post(
            `${BASE_URL}/api/v1/timesheet/pre/calculation/timesheet`,
            monthlyTimesheetIdList
        );
        return response.data;
    } catch (error) {
        console.log("error on getting pre calculation list", error);
        throw error;
    }
}


export async function getAllKindergartenAnalytics(
    searchParam: SearchParam
): Promise<PageResponse<KindergartenAnalyticsResponse>> {
    try {
        const response = await apiClient.post<PageResponse<KindergartenAnalyticsResponse>>(
            `${BASE_URL}/api/v1/kindergarten/analytics`, searchParam
        );
        return response.data;
    } catch (error) {
        console.error("Error getting KindergartenCalculation:", error);
        throw error;
    }
}